/**
 * ContainerTable — таблица docker-контейнеров с метриками и действиями.
 *
 * Backend: GET /api/v1/devops/containers, POST /api/v1/devops/container/{id}/{action},
 * GET /api/v1/devops/container/{id}/logs.
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  RefreshCw,
  Play,
  Square,
  RotateCw,
  Trash2,
  FileText,
  Loader2,
  ShieldAlert,
  X,
} from "lucide-react";
import {
  fetchNativeContainers,
  fetchNativeContainerLogs,
  nativeContainerAction,
  type NativeContainer,
} from "../../lib/api";
import { cn } from "../../lib/cn";

export function ContainerTable() {
  const [items, setItems] = useState<NativeContainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [showAll, setShowAll] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ cid: string; name: string; action: string } | null>(null);
  const [logsOpen, setLogsOpen] = useState<{ cid: string; name: string } | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  async function load() {
    try {
      setError(null);
      const r = await fetchNativeContainers({ all: showAll, filter });
      setItems(r.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [filter, showAll]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 7000);
    return () => clearInterval(t);
  }, [autoRefresh, filter, showAll]);

  async function doAction(
    cid: string,
    name: string,
    action: "start" | "stop" | "restart" | "rm" | "kill",
    confirmed: boolean = false,
  ) {
    setBusy(`${cid}:${action}`);
    try {
      await nativeContainerAction(cid, action, { confirm: confirmed });
      setConfirm(null);
      setTimeout(load, 300);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes(" 409 ") || msg.includes("критич")) {
        setConfirm({ cid, name, action });
      } else {
        setError(msg);
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-fg-subtle" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Имя контейнера или образ…"
            className="w-full pl-8 pr-3 py-1.5 rounded-md border border-border/60 bg-bg-card/70 text-small focus:outline-none focus:border-accent-500"
          />
        </div>
        <label className="flex items-center gap-1.5 text-small text-fg-muted">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="rounded"
          />
          включая stopped
        </label>
        <button
          type="button"
          onClick={() => setAutoRefresh((v) => !v)}
          className={cn(
            "px-2 py-1.5 rounded-md border text-small inline-flex items-center gap-1.5",
            autoRefresh
              ? "border-accent-500/50 text-accent-500 bg-accent-500/10"
              : "border-border/60 text-fg-muted",
          )}
        >
          <RefreshCw className={cn("size-3.5", autoRefresh && "animate-spin-slow")} />
          {autoRefresh ? "live" : "пауза"}
        </button>
        <button
          type="button"
          onClick={load}
          className="px-2 py-1.5 rounded-md border border-border/60 text-fg-muted hover:bg-bg-subtle text-small"
        >
          <RefreshCw className="size-3.5" />
        </button>
        <span className="text-small text-fg-muted ml-auto">{items.length} контейнеров</span>
      </div>

      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-small text-danger">
          {error}
        </div>
      )}

      {confirm && (
        <ConfirmContainerDialog
          name={confirm.name}
          action={confirm.action}
          onConfirm={() =>
            doAction(confirm.cid, confirm.name, confirm.action as "stop" | "restart" | "kill" | "rm", true)
          }
          onCancel={() => setConfirm(null)}
        />
      )}

      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-small font-mono">
            <thead className="bg-bg-subtle/60">
              <tr className="text-left text-fg-subtle text-[11px] uppercase tracking-wider">
                <th className="px-3 py-2">NAME</th>
                <th className="px-3 py-2">STATE</th>
                <th className="px-3 py-2">IMAGE</th>
                <th className="px-3 py-2 text-right">CPU%</th>
                <th className="px-3 py-2 text-right">MEM%</th>
                <th className="px-3 py-2">PORTS</th>
                <th className="px-3 py-2">UPTIME</th>
                <th className="px-3 py-2 text-right">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-fg-muted">
                    <Loader2 className="size-4 animate-spin inline-block mr-2" /> загрузка…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-fg-muted">
                    Нет контейнеров под фильтр
                  </td>
                </tr>
              ) : (
                items.map((c) => (
                  <ContainerRow
                    key={c.id}
                    c={c}
                    busy={busy?.startsWith(c.id + ":") ? busy.split(":")[1] : null}
                    onAction={(a) => doAction(c.id, c.name, a)}
                    onLogs={() => setLogsOpen({ cid: c.id, name: c.name })}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {logsOpen && (
        <ContainerLogsDrawer
          cid={logsOpen.cid}
          name={logsOpen.name}
          onClose={() => setLogsOpen(null)}
        />
      )}
    </div>
  );
}

function ContainerRow({
  c,
  busy,
  onAction,
  onLogs,
}: {
  c: NativeContainer;
  busy: string | null;
  onAction: (a: "start" | "stop" | "restart" | "rm") => void;
  onLogs: () => void;
}) {
  const isRunning = c.state === "running";
  const cpu = c.cpu_percent ?? 0;
  const mem = c.mem_percent ?? 0;
  return (
    <tr className="border-t border-border/30 hover:bg-bg-subtle/30">
      <td className="px-3 py-1.5 max-w-[200px] truncate text-fg" title={c.name}>
        {c.name}
      </td>
      <td className="px-3 py-1.5">
        <span
          className={cn(
            "px-1.5 py-0.5 rounded text-[11px] uppercase tracking-wider",
            isRunning
              ? "bg-success/15 text-success"
              : c.state === "exited" || c.state === "dead"
                ? "bg-fg-subtle/15 text-fg-subtle"
                : "bg-warning/15 text-warning",
          )}
        >
          {c.state || c.status}
        </span>
      </td>
      <td className="px-3 py-1.5 max-w-[220px] truncate text-fg-muted" title={c.image}>
        {c.image}
      </td>
      <td
        className={cn(
          "px-3 py-1.5 text-right tabular-nums",
          cpu >= 50 && "text-warning font-semibold",
        )}
      >
        {c.cpu_percent !== null ? cpu.toFixed(1) : "—"}
      </td>
      <td
        className={cn(
          "px-3 py-1.5 text-right tabular-nums",
          mem >= 25 && "text-warning font-semibold",
        )}
      >
        {c.mem_percent !== null ? mem.toFixed(1) : "—"}
      </td>
      <td className="px-3 py-1.5 max-w-[180px] truncate text-fg-muted" title={c.ports}>
        {c.ports || "—"}
      </td>
      <td className="px-3 py-1.5 whitespace-nowrap text-fg-muted">{c.running_for || "—"}</td>
      <td className="px-3 py-1.5 text-right whitespace-nowrap">
        {!isRunning && (
          <CtnBtn
            icon={<Play className="size-3" />}
            label="start"
            loading={busy === "start"}
            onClick={() => onAction("start")}
            tone="accent"
          />
        )}
        {isRunning && (
          <>
            <CtnBtn
              icon={<RotateCw className="size-3" />}
              label="restart"
              loading={busy === "restart"}
              onClick={() => onAction("restart")}
              tone="warning"
            />
            <CtnBtn
              icon={<Square className="size-3" />}
              label="stop"
              loading={busy === "stop"}
              onClick={() => onAction("stop")}
              tone="warning"
            />
          </>
        )}
        <CtnBtn
          icon={<Trash2 className="size-3" />}
          label="rm"
          loading={busy === "rm"}
          onClick={() => onAction("rm")}
          tone="danger"
        />
        <CtnBtn
          icon={<FileText className="size-3" />}
          label="logs"
          loading={false}
          onClick={onLogs}
          tone="muted"
        />
      </td>
    </tr>
  );
}

function CtnBtn({
  icon,
  label,
  loading,
  onClick,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  loading: boolean;
  onClick: () => void;
  tone: "accent" | "warning" | "danger" | "muted";
}) {
  const cls = {
    accent: "border-accent-500/40 text-accent-500 hover:bg-accent-500/10",
    warning: "border-warning/40 text-warning hover:bg-warning/10",
    danger: "border-danger/40 text-danger hover:bg-danger/10",
    muted: "border-border/60 text-fg-muted hover:bg-bg-subtle",
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={cn(
        "ml-1 px-2 py-1 rounded-md text-[11px] border inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-wait",
        cls,
      )}
    >
      {loading ? <Loader2 className="size-3 animate-spin" /> : icon}
      {label}
    </button>
  );
}

function ConfirmContainerDialog({
  name,
  action,
  onConfirm,
  onCancel,
}: {
  name: string;
  action: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-warning/40 bg-warning/5 p-3 flex items-start gap-2"
    >
      <ShieldAlert className="size-4 mt-0.5 shrink-0 text-warning" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-small text-fg">
          Критичный контейнер — подтвердите {action}
        </div>
        <div className="text-[12px] text-fg-muted mt-0.5">{name}</div>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-md text-small font-medium text-white"
            style={{ backgroundImage: "var(--grad-cta)" }}
          >
            Подтверждаю
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md text-small border border-border/60 text-fg-muted hover:bg-bg-subtle"
          >
            Отмена
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function ContainerLogsDrawer({
  cid,
  name,
  onClose,
}: {
  cid: string;
  name: string;
  onClose: () => void;
}) {
  const [logs, setLogs] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [n, setN] = useState(200);

  async function load() {
    try {
      setLoading(true);
      setError(null);
      const r = await fetchNativeContainerLogs(cid, n);
      setLogs(r.logs);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [cid, n]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        transition={{ type: "spring", damping: 22, stiffness: 220 }}
        onClick={(e) => e.stopPropagation()}
        className="relative ml-auto h-full w-full max-w-3xl bg-bg shadow-2xl flex flex-col"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border/40">
          <FileText className="size-4 text-accent-500" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{name}</div>
            <div className="text-[11px] text-fg-subtle font-mono uppercase tracking-wider">
              docker logs --tail {n} {cid.slice(0, 12)}
            </div>
          </div>
          <select
            value={n}
            onChange={(e) => setN(Number(e.target.value))}
            className="px-2 py-1 rounded border border-border/60 bg-bg-card text-small"
          >
            <option value={50}>50</option>
            <option value={200}>200</option>
            <option value={1000}>1000</option>
            <option value={5000}>5000</option>
          </select>
          <button
            type="button"
            onClick={load}
            className="p-1.5 rounded border border-border/60 hover:bg-bg-subtle"
            title="Обновить"
          >
            <RefreshCw className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded border border-border/60 hover:bg-bg-subtle"
            title="Закрыть"
          >
            <X className="size-3.5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-bg-subtle/40 p-3">
          {loading && (
            <div className="text-fg-muted text-small">
              <Loader2 className="size-4 animate-spin inline-block mr-2" /> загрузка…
            </div>
          )}
          {error && <div className="text-danger text-small">{error}</div>}
          {!loading && !error && (
            <pre className="text-[12px] font-mono whitespace-pre-wrap text-fg leading-relaxed">
              {logs || "(пусто)"}
            </pre>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
